import { useMemo } from "react";
import * as THREE from "three";

const VERTEX_SHADER = /* glsl */ `
  varying vec3 vNormalView;
  varying vec3 vViewDir;
  void main() {
    vec4 viewPos = modelViewMatrix * vec4(position, 1.0);
    vNormalView = normalize(normalMatrix * normal);
    vViewDir = normalize(-viewPos.xyz);
    gl_Position = projectionMatrix * viewPos;
  }
`;

/**
 * Banded fresnel rim rather than physical refraction. MeshPhysicalMaterial's
 * `transmission` reads as photoreal and fights the flat toon bands, so glass
 * here is: a soft tinted body, a hard-stepped rim, and one banded highlight
 * streak — all of which survive the cel-shaded look.
 */
const FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 tintColor;
  uniform vec3 rimColor;
  uniform float bodyOpacity;
  varying vec3 vNormalView;
  varying vec3 vViewDir;

  void main() {
    float fresnel = 1.0 - clamp(dot(normalize(vNormalView), normalize(vViewDir)), 0.0, 1.0);

    // Rim only on the true silhouette edge. A lower threshold lights up most
    // of a curved tube's surface and the glass reads as solid white plastic.
    float rim = smoothstep(0.78, 0.96, fresnel);

    // A single banded highlight streak, angled across the surface.
    float highlight = step(0.88, dot(normalize(vNormalView), normalize(vec3(0.6, 0.8, 0.2))));

    vec3 color = mix(tintColor, rimColor, rim * 0.8);
    color = mix(color, vec3(1.0), highlight * 0.35);

    float alpha = clamp(bodyOpacity + rim * 0.5 + highlight * 0.2, 0.0, 0.85);
    gl_FragColor = vec4(color, alpha);
  }
`;

export function useGlassMaterial(tint = "#BFE3F2", rim = "#FFFFFF", bodyOpacity = 0.28) {
  return useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          tintColor: { value: new THREE.Color(tint) },
          rimColor: { value: new THREE.Color(rim) },
          bodyOpacity: { value: bodyOpacity },
        },
        vertexShader: VERTEX_SHADER,
        fragmentShader: FRAGMENT_SHADER,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    [tint, rim, bodyOpacity],
  );
}
